<template>
    <div class="container">
        <div class="wrap">
            <div class="pb-8 medium:flex medium:items-center medium:justify-between medium:gap-5 medium:pb-11">
                <VendorFiltering
                    v-if="$root?.config?.schema?.blocks?.length > 0"
                    :activeVendor="activeVendor"
                    :vendorData="$root.config.schema.blocks"
                    @selected-vendor="selectedVendor"
                />
                <div class="flex items-center justify-between gap-x-5 shrink-0 mediumMax:mt-5">
                    <button
                        type="button"
                        class="button button--secondary button--small [&_svg]:fill-current"
                        @click.prevent="openFiltersModal()"
                    >
                        <svg width="22" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="4" height="1" rx="0.5" transform="matrix(1 0 0 -1 0 3.5)" fill="currentColor" />
                            <rect width="14" height="1" rx="0.5" transform="matrix(1 0 0 -1 8 3.5)" fill="currentColor" />
                            <rect width="4" height="1" rx="0.5" transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 22 8.5)" fill="currentColor" />
                            <rect width="14" height="1" rx="0.5" transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 14 8.5)" fill="currentColor" />
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M6 5.5C7.38071 5.5 8.5 4.38071 8.5 3C8.5 1.61929 7.38071 0.5 6 0.5C4.61929 0.5 3.5 1.61929 3.5 3C3.5 4.38071 4.61929 5.5 6 5.5ZM6 4.66667C6.92047 4.66667 7.66667 3.92047 7.66667 3C7.66667 2.07953 6.92047 1.33333 6 1.33333C5.07953 1.33333 4.33333 2.07953 4.33333 3C4.33333 3.92047 5.07953 4.66667 6 4.66667Z" fill="currentColor" />
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M16 6.5C14.6193 6.5 13.5 7.61929 13.5 9C13.5 10.3807 14.6193 11.5 16 11.5C17.3807 11.5 18.5 10.3807 18.5 9C18.5 7.61929 17.3807 6.5 16 6.5ZM16 7.33333C15.0795 7.33333 14.3333 8.07953 14.3333 9C14.3333 9.92047 15.0795 10.6667 16 10.6667C16.9205 10.6667 17.6667 9.92047 17.6667 9C17.6667 8.07953 16.9205 7.33333 16 7.33333Z" fill="currentColor" />
                        </svg>
                        <span class="flex-shrink-0 text-sm font-normal">
                            {{ $root.config.translations.filters.button_label }}
                        </span>
                    </button>
                    <span v-if="productCount" class="body-r">
                        {{ productCount }}
                    </span>
                </div>
            </div>
        </div>
    </div>
    <div class="container">
        <div class="wrap">
            <section>
                <div class="large:grid large:grid-cols-12 large:gap-5">
                    <div class="large:col-span-8">
                        <template v-if="loadingCollection">
                            <div class="flex justify-center items-center h-[12.5rem]">
                                <div class="w-10">
                                    <div class="loader">
                                        <span class="sr-only">
                                            {{ $root.config.translations.general.loading_products }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="products && !error">
                            <div v-if="products && products.length > 0" class="flex flex-wrap gap-5">
                                <ProductCard
                                    v-for="(product, index) in products"
                                    :key="index"
                                    :bundleIndex="getProductBundleIndex(product.id)"
                                    :currencySymbol="currencySymbol"
                                    :productData="product"
                                    :selectedVariants="selectedVariants"
                                    @added-to-bundle="addToBundle"
                                    @removed-bundle-item="removeProductFromBundle"
                                />
                            </div>
                            <div v-else>
                                <p class="body-l">
                                    {{ $root.config.translations.general.no_products }}
                                </p>
                            </div>
                        </template>
                        <template v-else-if="error">
                            <p class="body-l">
                                {{ error }}
                            </p>
                        </template>
                        <template v-else>
                            <p class="body-l">
                                {{ $root.config.translations.general.no_products }}
                            </p>
                        </template>
                    </div>
                    <div class="relative largeMax:hidden large:col-span-4">
                        <BundleBagPanel
                            :currencySymbol="currencySymbol"
                            :productBundle="productBundle"
                            :bundleBagPriceTotal="Number(bundleBagPriceTotal.total)"
                            :bundleBagPriceTotalCurrency="bundleBagPriceTotalCurrency"
                            :bundleBagPriceTotalCompareAtCurrency="bundleBagPriceTotalCompareAtCurrency"
                            @amended-bundle-item-quantity="amendBundleItemQuantity"
                            @removed-bundle-item="removeProductFromBundle"
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
    <FiltersOverlay
        v-if="products"
        ref="filtersModal"
        :activeVendor="activeVendor"
        :productCount="productCount"
        :products="products"
        @form-change="handleFilterChange"
        @form-reset="resetForm"
    />
    <BundleBagOverlay
        :currencySymbol="currencySymbol"
        :productBundle="productBundle"
        :bundleBagPriceTotal="Number(bundleBagPriceTotal.total)"
        :bundleBagPriceTotalCurrency="bundleBagPriceTotalCurrency"
        :bundleBagPriceTotalCompareAtCurrency="bundleBagPriceTotalCompareAtCurrency"
        @amended-bundle-item-quantity="amendBundleItemQuantity"
        @removed-bundle-item="removeProductFromBundle"
    />
</template>

<script>
    import BundleBagPanel from '@/apps/bundle-builder/components/BundleBagPanel.vue';
    import BundleBagOverlay from '@/apps/bundle-builder/components/BundleBagOverlay.vue';
    import FiltersOverlay from '@/apps/bundle-builder/components/FiltersOverlay.vue';
    import ProductCard from '@/apps/bundle-builder/components/ProductCard.vue';
    import VendorFiltering from '@/apps/bundle-builder/components/VendorFiltering.vue';

    export default {
        name: 'BundleBuilder',
        components: {
            BundleBagPanel,
            BundleBagOverlay,
            FiltersOverlay,
            ProductCard,
            VendorFiltering
        },
        data () {
            return {
                activeVendor: null,
                allProducts: null,
                checkedFilters: {
                    bottleMaterial: [],
                    category: [],
                    certificationAndStandards: [],
                    flowRate: [],
                    formulaLevel: [],
                    infantAge: [],
                    material: [],
                    milkType: [],
                    nippleType: [],
                    pacifierDesign: []
                },
                config: JSON.parse(document.getElementById('bundleBuilderConfig').text),
                currencySymbol: ['€'],
                error: null,
                loadingCollection: null,
                products: null,
                productBundle: [],
                selectedVariants: [],
                currentRoute : window.Shopify.routes.root || '/',
            }
        },
        created () {
            if (this.$root?.config?.schema?.blocks[0]?.filter_value) {
                // Set activeVendor as first block in the schema.
                this.activeVendor = this.$root.config.schema.blocks[0].filter_value;
            }

            this.fetchData();
        },
        mounted () {
            // Add padding on mobile to account for fixed bundle cart element
            if (window.innerWidth <= 1023) {
                document.body.style = 'padding-bottom: 97px;';
            }
        },
        watch: {
            activeVendor (newVendor, oldVendor) {
                if (newVendor != oldVendor) {
                    this.fetchData();
                }
            }
        },
        computed: {
            bundleBagPriceTotal () {
                let total = 0.00;
                let totalCompareAt = null;

                if (this.productBundle && this.productBundle.length > 0) {
                    this.productBundle.forEach(item => {
                        let price;
                        let priceCompareAt;

                        if (this.selectedProductVariant(item).selling_plan_allocations[0]?.price) {
                            price = (this.selectedProductVariant(item).selling_plan_allocations[0]?.price / 100).toFixed(2);
                            if (this.selectedProductVariant(item).selling_plan_allocations[0]?.compare_at_price) {
                                priceCompareAt = (this.selectedProductVariant(item).selling_plan_allocations[0].compare_at_price / 100).toFixed(2);
                            } else {
                                priceCompareAt = (this.selectedProductVariant(item).selling_plan_allocations[0].price / 100).toFixed(2);
                            }
                        } else if (this.selectedProductVariant(item).price) {
                            price = (this.selectedProductVariant(item).price / 100).toFixed(2);
                            priceCompareAt = (this.selectedProductVariant(item).price / 100).toFixed(2);
                        }

                        if (price) {
                            total += (price * item.quantity);
                        }

                        if (priceCompareAt) {
                            totalCompareAt += (priceCompareAt * item.quantity);
                        }
                    });
                }
                return { total: total.toFixed(2), totalCompareAt: totalCompareAt?.toFixed(2) };
            },
            bundleBagPriceTotalCompareAtCurrency () {
                if (this.bundleBagPriceTotal.totalCompareAt) {
                    return `${this.bundleBagPriceTotal.totalCompareAt}${this.currencySymbol}`;
                } else {
                    return null;
                }
            },
            bundleBagPriceTotalCurrency () {
                return `${this.bundleBagPriceTotal.total}${this.currencySymbol}`;
            },
            hasCheckedFilters () {
                return Object.values(this.checkedFilters).some(filters => filters.length > 0);
            },
            productCount () {
                let string;
                if (this.products && this.products.length) {
                    string = `${this.products.length} ${this.products.length >= 2 ? this.$root.config.translations.general.products : this.$root.config.translations.general.product}`;
                } else {
                    string = `0 ${this.$root.config.translations.general.products}`;
                }
                return string;
            }
        },
        methods: {
            async fetchData () {
                if (!this.$root?.config?.schema.bundle_collection || !this.activeVendor) {
                    this.loadingCollection = false;
                    return;
                }

                // Fetch product data from JSON collections page, structure can be found in template 'collection.json.liquid'.
                this.error = null;
                this.loadingCollection = true;

                try {
                    let response = await fetch(`${this.currentRoute}collections/${this.$root.config.schema.bundle_collection}?sort_by=manual&filter.p.vendor=${this.activeVendor}&view=json`, {
                        priority: 'high'
                    });

                    if (!response.ok) {
                        throw new Error(`Status ${response.status}`);
                    }

                    response = await response.json();

                    this.products = await response.products;
                    this.allProducts = await response.products;
                    this.currencySymbol = await response.currencySymbol;
                } catch (error) {
                    this.error = error;
                } finally {
                    this.loadingCollection = false;
                }
            },
            amendBundleItemQuantity (productIndex, change) {
                if (change == -1 && this.productBundle[productIndex].quantity == 1) {
                    this.productBundle.splice(productIndex, 1);
                    this.selectedVariants.splice(productIndex, 1);
                } else {
                    this.productBundle[productIndex].quantity += change;
                }
            },
            addToBundle (product, quantity, variantId) {
                const productInBundle = this.productBundle.find((item) => item.variantId === variantId);

                if (productInBundle && variantId) {
                    // Check if product variant is already in bundle and amend quantity
                    const productBundleIndex = this.productBundle.indexOf(productInBundle);
                    this.productBundle[productBundleIndex].quantity = this.productBundle[productBundleIndex].quantity += quantity;
                } else {
                    // Add to bundle
                    this.productBundle.push(
                        {
                            product: product,
                            quantity: quantity,
                            variantId: variantId
                        }
                    );
                    // Add selected variant to array
                    this.selectedVariants.push(Number(variantId));
                }
            },
            getProductBundleIndex (variantId) {
                if (variantId && this.productBundle.length > 0) {
                    const productInBundle = this.productBundle.find((item) => item.variantId === variantId);

                    if (productInBundle) {
                        // Check if product variant is in the bundle and return its position
                        const productBundleIndex = this.productBundle.indexOf(productInBundle);
                        return productBundleIndex;
                    } else {
                        return null;
                    }
                } else {
                    return null;
                }
            },
            clearFilters () {
                // Reset all checked flters
                Object.keys(this.checkedFilters).forEach(filterKey => {
                    this.checkedFilters[filterKey] = [];
                });

                // Reset all checked properties on filters within formData
                Object.keys(this.$refs.filtersModal.formData).forEach(filterKey => {
                    if (this.$refs.filtersModal.formData[filterKey]) {
                        this.$refs.filtersModal.formData[filterKey].forEach(filterItem => {
                            filterItem.checked = false;
                        });
                    }
                });
            },
            handleFilterChange (currentLabel, currentType, currentStatus) {
                this.loadingCollection = true;

                this.updatedCheckedFilters(currentLabel, currentType, currentStatus);

                // Check to see if products match current filters
                const filteredProducts = this.allProducts.filter(product => {
                    // Iterate through all the filters in checkedFilters
                    return Object.keys(this.checkedFilters).every(filterKey => {
                        const filterValues = this.checkedFilters[filterKey];

                        // If there are no selected values for this filter, skip it
                        if (!filterValues || filterValues.length === 0) {
                            return true;
                        }

                        // Dynamically check if the product's corresponding filter contains all the selected values
                        return filterValues.every(value => product.filters[filterKey]?.includes(value));
                    });
                });

                // Assign new products list or reset to all if no filters
                if (!currentStatus && !this.hasCheckedFilters) {
                    this.products = this.allProducts;
                } else if (filteredProducts && filteredProducts.length > 0) {
                    this.products = filteredProducts;
                }

                this.loadingCollection = false;
            },
            openFiltersModal () {
                if (this.$refs.filtersModal && this.$refs.filtersModal.$refs.modal) {
                    this.$refs.filtersModal.$refs.modal.openDialog();
                }
            },
            removeProductFromBundle (index) {
                this.productBundle.splice(index, 1);
                this.selectedVariants.splice(index, 1);
            },
            resetForm () {
                this.clearFilters();
                this.products = this.allProducts;
            },
            selectedProductVariant (item) {
                // Return selected variant of a product used in bundle total calculation
                return item.product.variants.find((variant) => variant.id === Number(item.variantId));
            },
            selectedVendor (oldVendor, newVendor) {
                this.activeVendor = newVendor;

                if (oldVendor !== this.activeVendor) {
                    this.clearFilters();
                }
            },
            updatedCheckedFilters (currentLabel, currentType, currentStatus) {
                if (currentStatus) {
                    this.checkedFilters[currentType].push(currentLabel);
                } else {
                    const index = this.checkedFilters[currentType].indexOf(currentLabel);
                    this.checkedFilters[currentType].splice(index, 1);
                }
            }
        }
    };
</script>
